import React from 'react';
import { Shield, Lock, FileText, Scale, Info } from 'lucide-react';

const LegalLayout: React.FC<{ title: string; lastUpdated: string; children: React.ReactNode }> = ({ title, lastUpdated, children }) => (
    <div className="min-h-screen bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12 border-b border-gray-100 pb-8">
                <h1 className="text-4xl font-extrabold text-[#2f5c96] mb-4">{title}</h1>
                <p className="text-gray-500 text-sm">Last Updated: {lastUpdated}</p>
            </div>
            <div className="prose prose-blue max-w-none text-gray-600 space-y-8">
                {children}
            </div>
        </div>
    </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 border-l-4 border-[#c19cca] pl-4">{title}</h2>
        <div className="text-gray-600 leading-relaxed space-y-2">
            {children}
        </div>
    </div>
);

export const TermsAndConditions: React.FC = () => (
    <LegalLayout title="Terms and Conditions" lastUpdated="December 31, 2025">
        <Section title="1. Agreement to Terms">
            <p>By accessing or using the Indigo Beans Rooms platform, you agree to be bound by these Terms and Conditions. If you do not agree, please do not use our services.</p>
        </Section>
        <Section title="2. Use of the Platform">
            <p>Indigo Beans Rooms provides a platform connecting students, agents, and landlords. We do not own, manage, or control the properties listed on the site.</p>
            <ul className="list-disc pl-6 space-y-2">
                <li>You must be at least 18 years old to create an account.</li>
                <li>You are responsible for maintaining the confidentiality of your account details.</li>
                <li>You agree to provide accurate and truthful information in all listings and communications.</li>
            </ul>
        </Section>
        <Section title="3. Bookings and Payments">
            <p>All bookings made through the platform are agreements between the seeker (Student) and the provider (Landlord/Agent). Indigo Beans Rooms acts only as an intermediary.</p>
        </Section>
        <Section title="4. Prohibited Activities">
            <p>Users are prohibited from posting fraudulent listings, engaging in harassment, or attempting to bypass the platform's security or payment systems.</p>
        </Section>
        <Section title="5. Limitation of Liability">
            <p>Indigo Beans Rooms is not liable for any disputes, damages, or losses arising from the use of the platform or the properties listed herein.</p>
        </Section>
    </LegalLayout>
);

export const PrivacyPolicy: React.FC = () => (
    <LegalLayout title="Privacy Policy" lastUpdated="December 31, 2025">
        <Section title="1. Information We Collect">
            <p>We collect information you provide directly to us when creating an account, listing a property, or making a booking. This includes name, email, phone number, and identity verification documents.</p>
        </Section>
        <Section title="2. How We Use Your Information">
            <p>Your information is used to facilitate bookings, verify identities, improve our services, and communicate important updates regarding your account or the platform.</p>
        </Section>
        <Section title="3. Information Sharing">
            <p>We share necessary information between students and landlords to facilitate bookings. We do not sell your personal data to third parties.</p>
        </Section>
        <Section title="4. Data Security">
            <p>We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, or disclosure.</p>
        </Section>
    </LegalLayout>
);

export const RefundPolicy: React.FC = () => (
    <LegalLayout title="Refund Policy" lastUpdated="December 31, 2025">
        <Section title="1. Booking Fees">
            <p>Platform booking fees are generally non-refundable unless the booking is canceled by the landlord or due to a verified error on our part.</p>
        </Section>
        <Section title="2. Rental Deposits">
            <p>Refunds for rental deposits and first-month rent are subject to the specific lease agreement signed between the student and the landlord.</p>
        </Section>
        <Section title="3. Dispute Resolution">
            <p>If a dispute arises regarding a refund, Indigo Beans Rooms will attempt to mediate, but the final decision rests with the terms of the individual lease agreement.</p>
        </Section>
    </LegalLayout>
);

export const PopiCompliance: React.FC = () => (
    <LegalLayout title="POPI Act Compliance" lastUpdated="December 31, 2025">
        <Section title="Introduction">
            <p>In accordance with the Protection of Personal Information Act (POPIA) of South Africa, Indigo Beans Property Group is committed to protecting the privacy of our users.</p>
        </Section>
        <Section title="Our Commitment">
            <ul className="list-disc pl-6 space-y-2">
                <li><strong>Accountability:</strong> We take full responsibility for the personal information we process.</li>
                <li><strong>Processing Limitation:</strong> We only process information for specific, lawful purposes related to our business.</li>
                <li><strong>Purpose Specification:</strong> Personal information is collected for the purpose of facilitating property rentals and related services.</li>
                <li><strong>Openness:</strong> We are transparent about the information we collect and how it is used.</li>
            </ul>
        </Section>
        <Section title="Your Rights">
            <p>Users have the right to access, correct, or request the deletion of their personal information at any time. To exercise these rights, please contact our Information Officer at rooms@indigobeans.co.za.</p>
        </Section>
    </LegalLayout>
);

export const CookiePolicy: React.FC = () => (
    <LegalLayout title="Cookie Policy" lastUpdated="December 31, 2025">
        <Section title="What are Cookies?">
            <p>Cookies are small text files stored on your device that help us improve your experience on our platform.</p>
        </Section>
        <Section title="Types of Cookies We Use">
            <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> Necessary for the platform to function correctly.</li>
                <li><strong>Analytical Cookies:</strong> Help us understand how users interact with our site.</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings.</li>
            </ul>
        </Section>
        <Section title="Managing Cookies">
            <p>You can choose to disable cookies through your browser settings, although this may affect the functionality of certain parts of our platform.</p>
        </Section>
    </LegalLayout>
);
